import { Box } from '@material-ui/core'
import { maxMessageLength } from '@shared/core/events'
import { useSnackbar } from 'notistack'
import { ChangeEvent, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { AppointmentConfirmationStepsModal } from 'src/modules/confirmation/containers'
import { Button, FormattedText, FormControl, Icon, ToggleSwitch, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { generateEstimatedEventMessage } from 'src/modules/event/utils/message-builder'
import { useBookingOnboarding, useUpdateBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import { useOptimisticUpdateProfile } from 'src/modules/user/hooks/use-optimistic-update-profile'
import type { ProfileFormData } from 'src/modules/user/types/profile.types'
import { AppointmentRescheduleModal } from '../settings-profile-info/appointment-reschedule-modal'
import { EventTimezoneModal } from '../settings-profile-info/event-timezone-modal'
import type { OtherSettingsProfileFormHandle } from '../settings-profile-info/other-settings-form-handles'
import type { SettingsProfileInfoFormProps } from './settings-profile-info-form.interface'
import classes from './settings-profile-info-form.module.scss'

export const SettingsProfileInfoFormCustomerMode = forwardRef<
  OtherSettingsProfileFormHandle,
  SettingsProfileInfoFormProps
>(function SettingsProfileInfoFormCustomerMode(
  {
    profileInfo,
    preferences,
    setIsProfileInfoEditing,
    onFormValuesChange,
    onCancel,
    hideSubmitButton = false,
    onDirtyChange,
  },
  ref,
) {
  const navigate = useNavigate()
  const optimisticUpdateProfile = useOptimisticUpdateProfile()
  const updateBookingOnboarding = useUpdateBookingOnboarding()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { data: bookingOnboarding } = useBookingOnboarding()
  const onboardingCompleted = bookingOnboarding?.onboarding_completed === true

  const [isAppointmentConfirmationModalOpen, setIsAppointmentConfirmationModalOpen] = useState(false)
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false)
  const [isTimeZoneModalOpen, setIsTimeZoneModalOpen] = useState(false)

  const onFormValuesChangeRef = useRef(onFormValuesChange)
  onFormValuesChangeRef.current = onFormValuesChange

  const {
    control,
    handleSubmit,
    watch,
    setError,
    reset,
    getValues,
    formState: { isDirty, errors, dirtyFields },
  } = useForm<ProfileFormData>({
    defaultValues: {
      appointmentConfirmation: !!profileInfo.appointment_confirmation,
      appointmentConfirmationReschedule: !!bookingOnboarding?.is_rescheduling_enabled,
      useEventTimeZone: !!profileInfo.use_event_timezone,
    },
  })
  const { defaultReminder, meetingLocation, from } = watch()

  const appointmentConfirmation = watch('appointmentConfirmation')
  const appointmentConfirmationReschedule = watch('appointmentConfirmationReschedule')
  const useEventTimeZone = watch('useEventTimeZone')

  useEffect(() => {
    if (onFormValuesChangeRef.current) {
      onFormValuesChangeRef.current({
        useEventTimeZone,
        appointmentConfirmation: !!appointmentConfirmation,
        appointmentConfirmationReschedule: !!appointmentConfirmationReschedule,
      })
    }
  }, [useEventTimeZone, appointmentConfirmation, appointmentConfirmationReschedule])

  const { messageLength } = useMemo(
    () =>
      generateEstimatedEventMessage(
        'reminder',
        {
          ...profileInfo,
          appointment_confirmation: appointmentConfirmation,
          default_message_template: {
            ...profileInfo.default_message_template,
            sender_name: from,
            location: meetingLocation,
            reminder_message: defaultReminder,
          },
        },
        preferences,
        profileInfo.default_message_template?.language,
      ),
    [profileInfo, preferences, defaultReminder, from, meetingLocation, appointmentConfirmation],
  )

  const persistProfileIfDirty = useCallback(async (): Promise<boolean> => {
    const hasDirty = isDirty && Object.keys(dirtyFields).length !== 0
    if (!hasDirty) {
      setIsProfileInfoEditing?.(false)
      onCancel?.()
      return false
    }

    if (messageLength > maxMessageLength) {
      setError('defaultReminder', {
        type: 'custom',
        message: 'Default SMS Reminder must be shorter than 320 characters',
      })
      return false
    }

    const data = getValues()
    try {
      await Promise.all([
        optimisticUpdateProfile.mutateAsync({
          appointment_confirmation: Boolean(data.appointmentConfirmation),
          use_event_timezone: Boolean(data.useEventTimeZone),
        }),
        updateBookingOnboarding.mutateAsync({
          is_rescheduling_enabled: data.appointmentConfirmationReschedule ?? null,
        }),
      ])
      reset({
        appointmentConfirmation: data.appointmentConfirmation,
        appointmentConfirmationReschedule: data.appointmentConfirmationReschedule,
        useEventTimeZone: data.useEventTimeZone,
      })
      setIsProfileInfoEditing?.(false)
      onCancel?.()
      return true
    } catch {
      return false
    }
  }, [
    dirtyFields,
    getValues,
    isDirty,
    messageLength,
    onCancel,
    optimisticUpdateProfile,
    reset,
    setError,
    setIsProfileInfoEditing,
    updateBookingOnboarding,
  ])

  useImperativeHandle(
    ref,
    () => ({
      submitIfDirty: persistProfileIfDirty,
    }),
    [persistProfileIfDirty],
  )

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const onFormSubmit = handleSubmit(async () => {
    const saved = await persistProfileIfDirty()
    if (saved && !hideSubmitButton) {
      enqueueSnackbar(formatMessage({ id: 'settings.saved' }), { variant: 'success' })
    }
  })

  return (
    <>
      <form onSubmit={onFormSubmit}>
        <FormControl
          label={formatMessage({
            id: 'settings.appointment.confirmation',
          })}
          labelClassName={classes.label}
          error={errors?.appointmentConfirmation?.message}
        >
          <Box display="flex" alignItems="center" gridColumnGap={10} marginBottom={3}>
            <Controller
              control={control}
              name="appointmentConfirmation"
              render={({ field: { onChange, value, ref: fieldRef } }) => (
                <ToggleSwitch
                  ref={fieldRef}
                  checked={value}
                  labelClassName={classes.switchLabel}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                />
              )}
            />
            <Typography variant="body" className={classes.infoText}>
              <FormattedText id={watch('appointmentConfirmation') ? 'common.enabled' : 'common.disabled'} />
            </Typography>
            <Icon
              name="questionMark"
              className={classes.tooltipLink}
              onClick={() => setIsAppointmentConfirmationModalOpen(true)}
            />
          </Box>
        </FormControl>
        <FormControl
          label={formatMessage({
            id: 'settings.appointment.allowRescheduleFromConfirmation',
          })}
          labelClassName={classes.label}
        >
          {onboardingCompleted && appointmentConfirmationReschedule && !appointmentConfirmation && (
            <Typography variant="span" className={classes.rescheduleConfirmHint}>
              {formatMessage({
                id: 'settings.appointment.considerEnablingConfirmationsForReschedule',
              })}
            </Typography>
          )}
          {onboardingCompleted ? (
            <Box display="flex" alignItems="center" gridColumnGap={10} marginBottom={3}>
              <Controller
                control={control}
                name="appointmentConfirmationReschedule"
                render={({ field: { onChange, value, ref: fieldRef } }) => (
                  <ToggleSwitch
                    ref={fieldRef}
                    checked={value}
                    labelClassName={classes.switchLabel}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                  />
                )}
              />
              <Typography variant="body" className={classes.infoText}>
                <FormattedText id={appointmentConfirmationReschedule ? 'common.enabled' : 'common.disabled'} />
              </Typography>
              <Icon
                name="questionMark"
                className={classes.tooltipLink}
                onClick={() => setIsRescheduleModalOpen(true)}
              />
            </Box>
          ) : (
            <Box marginBottom={3}>
              <button
                type="button"
                className={classes.setupBookingLink}
                onClick={() => navigate(`${routeNames.settings.path}?tab=booking-page&enableReschedule=1`)}
              >
                <FormattedText id="settings.appointment.setupBookingPageForReschedule" />
              </button>
            </Box>
          )}
        </FormControl>
        <Box display="flex" alignItems="center" gridColumnGap={10} marginBottom={2}>
          <FormControl
            label={formatMessage({
              id: 'settings.eventTimeZone.label',
            })}
            labelClassName={classes.label}
            error={errors?.useEventTimeZone?.message}
          >
            <Box display="flex" alignItems="center" gridColumnGap={10}>
              <Controller
                control={control}
                name="useEventTimeZone"
                render={({ field: { onChange, value, ref: fieldRef } }) => (
                  <ToggleSwitch
                    ref={fieldRef}
                    checked={value}
                    labelClassName={classes.switchLabel}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                  />
                )}
              />
              <Typography variant="body" className={classes.infoText}>
                <FormattedText id={watch('useEventTimeZone') ? 'common.enabled' : 'common.disabled'} />
              </Typography>
              <Icon name="questionMark" className={classes.tooltipLink} onClick={() => setIsTimeZoneModalOpen(true)} />
            </Box>
          </FormControl>
        </Box>
        {!hideSubmitButton && (
          <Button
            type="submit"
            className={classes.button}
            widthType="content"
            isLoading={optimisticUpdateProfile.isLoading || updateBookingOnboarding.isLoading}
            disabled={!isDirty}
          >
            <FormattedText id="common.save" />
          </Button>
        )}
      </form>

      {isAppointmentConfirmationModalOpen && (
        <AppointmentConfirmationStepsModal onClose={() => setIsAppointmentConfirmationModalOpen(false)} />
      )}

      {isRescheduleModalOpen && <AppointmentRescheduleModal onClose={() => setIsRescheduleModalOpen(false)} />}

      {isTimeZoneModalOpen && <EventTimezoneModal onClose={() => setIsTimeZoneModalOpen(false)} />}
    </>
  )
})
